import React from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  LoadingSkeleton,
  EmptyState,
  EcosystemIcon,
} from '@redbax/ui-kit';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import type { PackageVulnerability } from '@redbax/shared';
import { formatDate } from '@redbax/shared';
import { usePackageVulnerabilities } from '../api/package-policy-hooks';
import { SeverityBadge } from '../components/severity-badge';

interface ParsedRange {
  introduced: string | null;
  fixed: string | null;
  lastAffected: string | null;
}

function parseAffectedVersions(raw: string | null): { ranges: ParsedRange[]; versions: string[] } {
  if (!raw) return { ranges: [], versions: [] };
  try {
    const parsed = JSON.parse(raw);
    const versions = (parsed.versions as string[] | undefined) ?? [];
    const rawRanges = (parsed.ranges as { events?: Record<string, string>[] }[] | undefined) ?? [];
    const ranges: ParsedRange[] = [];

    for (const range of rawRanges) {
      let current: ParsedRange = { introduced: null, fixed: null, lastAffected: null };
      for (const event of range.events ?? []) {
        if (event.introduced !== undefined) {
          if (current.introduced !== null) {
            ranges.push(current);
            current = { introduced: null, fixed: null, lastAffected: null };
          }
          current.introduced = event.introduced;
        }
        if (event.fixed !== undefined) {
          current.fixed = event.fixed;
        }
        if (event.last_affected !== undefined) {
          current.lastAffected = event.last_affected;
        }
      }
      if (current.introduced !== null) {
        ranges.push(current);
      }
    }

    return { ranges, versions };
  } catch {
    return { ranges: [], versions: [] };
  }
}

function formatRangeText(range: ParsedRange): string {
  const intro = range.introduced === '0' ? 'all versions' : `>=${range.introduced}`;
  if (range.fixed) return `${intro}, <${range.fixed}`;
  if (range.lastAffected) return `${intro}, <=${range.lastAffected}`;
  return intro;
}

function SeveritySummary({ vulnerabilities }: { vulnerabilities: PackageVulnerability[] }) {
  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, UNKNOWN: 0 };
  for (const v of vulnerabilities) {
    const sev = v.severity?.toUpperCase() ?? 'UNKNOWN';
    if (sev in counts) {
      counts[sev as keyof typeof counts]++;
    } else {
      counts.UNKNOWN++;
    }
  }

  return (
    <div className="flex flex-wrap gap-3">
      {counts.CRITICAL > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-red-accent/15 px-2.5 py-1 text-xs font-semibold text-red-accent">
            {counts.CRITICAL} Critical
          </span>
        </div>
      )}
      {counts.HIGH > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-orange-500/15 px-2.5 py-1 text-xs font-semibold text-orange-500">
            {counts.HIGH} High
          </span>
        </div>
      )}
      {counts.MEDIUM > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-yellow-500/15 px-2.5 py-1 text-xs font-semibold text-yellow-500">
            {counts.MEDIUM} Medium
          </span>
        </div>
      )}
      {counts.LOW > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-blue-500/15 px-2.5 py-1 text-xs font-semibold text-blue-500">
            {counts.LOW} Low
          </span>
        </div>
      )}
      {counts.UNKNOWN > 0 && (
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-gray-500/15 px-2.5 py-1 text-xs font-semibold text-gray-400">
            {counts.UNKNOWN} Unknown
          </span>
        </div>
      )}
    </div>
  );
}

function VulnerabilityCard({ vuln }: { vuln: PackageVulnerability }) {
  const { ranges, versions } = parseAffectedVersions(vuln.affectedVersions);
  const fixedVersions = ranges
    .filter((r) => r.fixed)
    .map((r) => r.fixed!);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={`https://osv.dev/vulnerability/${vuln.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-cyan-info hover:underline inline-flex items-center gap-1"
              >
                {vuln.id}
                <ExternalLink className="h-3 w-3" />
              </a>
              {vuln.aliases && vuln.aliases.length > 0 && vuln.aliases.map((alias) => (
                <Badge key={alias} variant="outline" className="text-[10px]">{alias}</Badge>
              ))}
            </div>

            {vuln.summary && (
              <p className="text-sm text-text-secondary leading-relaxed">{vuln.summary}</p>
            )}

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
              {ranges.length > 0 && (
                <div>
                  <span className="font-medium text-text-secondary">Affected: </span>
                  <span className="text-text-primary">
                    {ranges.map((r) => formatRangeText(r)).join(' | ')}
                  </span>
                </div>
              )}
              {versions.length > 0 && ranges.length === 0 && (
                <div>
                  <span className="font-medium text-text-secondary">Affected versions: </span>
                  <span className="text-text-primary max-w-[300px] truncate inline-block align-bottom" title={versions.join(', ')}>
                    {versions.join(', ')}
                  </span>
                </div>
              )}
              {fixedVersions.length > 0 && (
                <div>
                  <span className="font-medium text-text-secondary">Fixed in: </span>
                  <span className="text-green-ok font-medium">{fixedVersions.join(', ')}</span>
                </div>
              )}
              {vuln.publishedAt && (
                <div>
                  <span className="font-medium text-text-secondary">Published: </span>
                  <span className="text-text-primary">{formatDate(vuln.publishedAt)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 flex flex-col items-end gap-1.5">
            <SeverityBadge severity={vuln.severity} />
            {vuln.cvssScore != null && (
              <span className="text-xs text-text-secondary font-mono">{vuln.cvssScore.toFixed(1)}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PackageDetailPage() {
  const { ecosystem, packageName } = useParams<{ ecosystem: string; packageName: string }>();
  const navigate = useNavigate();

  const { data: vulnerabilities, isLoading } = usePackageVulnerabilities(
    ecosystem ?? '',
    packageName ?? '',
  );

  if (!ecosystem || !packageName) {
    return (
      <EmptyState
        title="Package not found"
        description="No ecosystem or package name specified."
      />
    );
  }

  const vulns = vulnerabilities ?? [];

  return (
    <div>
      <PageHeader
        title={packageName}
        description={`All known vulnerabilities for ${ecosystem}/${packageName}`}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        }
      />

      <div className="mb-6">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <EcosystemIcon ecosystem={ecosystem} size={20} />
                <span className="text-sm font-medium text-text-secondary">{ecosystem}</span>
                <span className="text-text-secondary">/</span>
                <span className="text-lg font-semibold text-text-primary">{packageName}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-text-primary">{vulns.length}</span>
                <span className="text-sm text-text-secondary">
                  vulnerabilit{vulns.length === 1 ? 'y' : 'ies'}
                </span>
              </div>
            </div>
            {vulns.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border-default">
                <SeveritySummary vulnerabilities={vulns} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <LoadingSkeleton rows={5} />
      ) : vulns.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-sm text-green-ok font-medium">
                No known vulnerabilities found for this package.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {vulns.map((vuln) => (
            <VulnerabilityCard key={vuln.id} vuln={vuln} />
          ))}
        </div>
      )}
    </div>
  );
}
